import { forwardRef, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { formatDecimalDraft, isDecimalDraft, parseDecimal } from "@/lib/decimal";

/**
 * A number field that keeps what the user typed.
 *
 * The parent owns a number; this owns the text. Keeping them apart is what
 * lets "12," or an empty field exist while typing — a controlled number input
 * snaps both back to the last valid value, so a field cannot be cleared.
 *
 * `emptyValue` is what the parent receives while the text spells no number.
 * The text is only rewritten from `value` when the parent changes it to
 * something this field did not just report (a reset, a record being loaded).
 */
export const DecimalInput = forwardRef(function DecimalInput(
  { value, onValueChange, emptyValue = null, allowNegative = false, ...props },
  ref,
) {
  const [draft, setDraft] = useState(() => formatDecimalDraft(value));
  const reported = useRef(value);

  useEffect(() => {
    if (value === reported.current) return;
    reported.current = value;
    setDraft(formatDecimalDraft(value));
  }, [value]);

  const handleChange = (event) => {
    const text = event.target.value;
    if (!isDecimalDraft(text, { allowNegative })) return;
    setDraft(text);
    reported.current = parseDecimal(text) ?? emptyValue;
    onValueChange(reported.current);
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      {...props}
      value={draft}
      onChange={handleChange}
    />
  );
});
